<script setup lang="ts">
import { ref, watch } from 'vue'
import { VButton, VDropdownMultiple, VIcon } from '@/modules/shared/components'
import { useMessagingStore } from '../stores/messaging-store'
import { RECEIVER_GROUP_OPTIONS, SUB_FILTER_KEYS } from '../types'
import type { MessageReceiverAttribute, ExtraInfo, ReceiverGroupOption } from '../types'

const props = defineProps<{
  modelValue: MessageReceiverAttribute[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: MessageReceiverAttribute[]]
}>()

const messagingStore = useMessagingStore()

interface Row {
  receiver_group: string
  extra_info: ExtraInfo
  lookupOptions: { label: string; value: string }[]
  loadingLookups: boolean
  selectedIds: string[]
}

const rows = ref<Row[]>(
  props.modelValue.length > 0
    ? props.modelValue.map((r) => ({
        receiver_group: r.receiver_group,
        extra_info: { ...r.extra_info },
        lookupOptions: [],
        loadingLookups: false,
        selectedIds: (r.extra_info.specific_ids as string[]) || [],
      }))
    : [{ receiver_group: '', extra_info: {}, lookupOptions: [], loadingLookups: false, selectedIds: [] }],
)

const addRow = () => {
  rows.value.push({ receiver_group: '', extra_info: {}, lookupOptions: [], loadingLookups: false, selectedIds: [] })
}

const removeRow = (index: number) => {
  rows.value.splice(index, 1)
  if (rows.value.length === 0) addRow()
  emitUpdate()
}

const getOption = (group: string): ReceiverGroupOption | undefined => {
  return RECEIVER_GROUP_OPTIONS.find((o) => o.value === group)
}

const fetchLookups = async (index: number, group: string) => {
  const row = rows.value[index]
  row.loadingLookups = true
  try {
    const items = await messagingStore.fetchLookups(group)
    row.lookupOptions = items.map((item) => ({ label: item.name, value: String(item.id) }))
  } catch {
    row.lookupOptions = []
  } finally {
    row.loadingLookups = false
  }
}

const onGroupChange = async (index: number, value: string) => {
  rows.value[index].receiver_group = value
  rows.value[index].extra_info = {}
  rows.value[index].selectedIds = []
  rows.value[index].lookupOptions = []

  if (getOption(value)?.has_specific_ids) {
    await fetchLookups(index, value)
  }

  emitUpdate()
}

const onSelectedIdsChange = (index: number, ids: string[]) => {
  rows.value[index].selectedIds = ids
  rows.value[index].extra_info.specific_ids = ids
  emitUpdate()
}

const onExtraInfoChange = (index: number, key: string, value: boolean | string) => {
  rows.value[index].extra_info[key] = value
  emitUpdate()
}

const emitUpdate = () => {
  const receivers = rows.value
    .filter((r) => r.receiver_group !== '')
    .map((r) => ({
      receiver_group: r.receiver_group as MessageReceiverAttribute['receiver_group'],
      extra_info: { ...r.extra_info },
    }))
  emit('update:modelValue', receivers)
}

watch(
  () => props.modelValue,
  (val) => {
    if (val.length === 0 && rows.value.length === 0) {
      rows.value = [{ receiver_group: '', extra_info: {}, lookupOptions: [], loadingLookups: false, selectedIds: [] }]
    }
  },
)
</script>

<template>
  <div class="space-y-3">
    <div v-for="(row, index) in rows" :key="index" class="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
      <div class="flex items-start gap-3">
        <div class="flex-1">
          <label class="mb-1 block text-sm font-medium text-gray-700">Recipient group</label>
          <select
            :value="row.receiver_group"
            @change="onGroupChange(index, ($event.target as HTMLSelectElement).value)"
            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#3b88af] focus:ring focus:ring-[#3b88af]/20 focus:ring-opacity-50"
          >
            <option value="">Select a group...</option>
            <option v-for="option in RECEIVER_GROUP_OPTIONS" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <button type="button" class="mt-6 text-gray-400 hover:text-red-500" @click="removeRow(index)">
          <VIcon name="Trash" class="h-4 w-4" />
        </button>
      </div>

      <!-- Sub-filters for group types that support them -->
      <div v-if="getOption(row.receiver_group)?.has_sub_filters" class="mt-3 flex flex-wrap gap-3">
        <label
          v-for="filter in SUB_FILTER_KEYS"
          :key="filter.key"
          class="flex items-center gap-1.5 text-sm text-gray-600"
        >
          <input
            type="checkbox"
            :checked="!!row.extra_info[filter.key]"
            @change="onExtraInfoChange(index, filter.key, ($event.target as HTMLInputElement).checked)"
            class="rounded border-gray-300 text-[#3b88af] focus:ring-[#3b88af]"
          />
          {{ filter.label }}
        </label>
      </div>

      <!-- Entity selector for specific_* groups -->
      <div v-if="getOption(row.receiver_group)?.has_specific_ids" class="mt-3">
        <label class="mb-1 block text-sm font-medium text-gray-700">Select entities</label>
        <div v-if="row.loadingLookups" class="text-sm text-gray-500">Loading...</div>
        <VDropdownMultiple
          v-else
          :modelValue="row.selectedIds"
          @update:modelValue="(ids: string[]) => onSelectedIdsChange(index, ids)"
          :options="row.lookupOptions"
          :show-selected-list="true"
          :config="{ closeAfterSelect: false }"
        >
          <VButton size="sm" class="w-full">
            <div class="flex items-center justify-between gap-3">
              <span>{{ row.selectedIds.length === 0 ? 'Select...' : `${row.selectedIds.length} selected` }}</span>
              <VIcon name="ChevronSelectorVertical" class="h-4 w-4" />
            </div>
          </VButton>
        </VDropdownMultiple>
      </div>
    </div>

    <button
      type="button"
      @click="addRow"
      class="flex items-center gap-1 text-sm text-[#3b88af] hover:text-[#3b88af]/80"
    >
      <VIcon name="Plus" class="h-3 w-3" />
      <span>Add additional recipients</span>
    </button>
  </div>
</template>
